import React, { memo, ReactElement, useMemo } from "react";
import classNames from "classnames";
import { getInfoTextFontStyles, getPositiveColorText } from "@core-ui/helpers/BillDetailsHelper";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ItemValue } from "../item-value";
import { Icon } from "../item-value/components/Icon";
import { isEmpty, checkIfNumber } from "@core-ui/helpers/Utils";
import { replaceText } from "@core-ui/helpers/HtmlFormatter";
import { RenderingDetails } from "@swiggy-private/cart-types";

import styles from "../BillDetailItem.scss";
import { RowDisplayText } from "./styles";

interface InfoDisplay {
    itemData: RenderingDetails;
    isInTooltip?: boolean;
    isNested?: boolean;
}

const InfoDisplay = ({ itemData, isInTooltip = false, isNested = false }: InfoDisplay): ReactElement => {
    const { value, intermediateText, meta, icon } = itemData;
    const containerClass = classNames({
        [styles.display]: true,
        [styles.displayInTooltip]: isInTooltip,
        [styles.displayInTooltipNested]: isInTooltip && isNested,
    });

    const rowStyle = classNames(styles.row, {
        [styles.rowNoGap]: isNested,
    });
    const isIntermediateTextNumber = useMemo((): boolean => checkIfNumber(intermediateText), [intermediateText]);

    const Name = useMemo(
        () => (
            <RowDisplayText className={styles.rowDisplayText}>
                {replaceText(itemData.display_text || "", "imageStyle")}
            </RowDisplayText>
        ),
        [itemData.display_text],
    );

    const Value = useMemo(() => {
        return (
            <ItemValue
                value={value}
                intermediateText={intermediateText}
                intermediateTextStyle={isIntermediateTextNumber ? styles.rupee : ""}
                isNegative={itemData.is_negative == 1}
                meta={meta}
                icon={!isNested ? icon : ""}
                isInTooltip={isInTooltip}
                itemKey={itemData.key}
                isNested={isNested}
            />
        );
    }, [
        value,
        intermediateText,
        isIntermediateTextNumber,
        itemData.is_negative,
        itemData.key,
        meta,
        isNested,
        icon,
        isInTooltip,
    ]);

    const SecondaryText = useMemo(() => {
        if (isEmpty(itemData.info_text)) {
            return null;
        }

        const style = getInfoTextFontStyles(itemData);
        const className = classNames({
            [styles.displayInfoText]: true,
            [styles.displayInfoTextInTooltip]: isInTooltip,
            [styles.displayInfoTextInTooltipNested]: isInTooltip && isNested,
        });

        return (
            <div
                className={className}
                style={style}
                // TODO: to update to generic contract post release
                dangerouslySetInnerHTML={{ __html: getPositiveColorText(itemData.info_text, itemData.key) }}
            />
        );
    }, [itemData, isInTooltip, isNested]);

    return (
        <div className={containerClass}>
            <div className={rowStyle}>
                <Text
                    variant={!isNested ? "V2.body2.neutral.medium" : "V2.body3.neutral.medium"}
                    color={!isNested ? "text_Med_Emphasis" : "text_Low_Emphasis"}
                >
                    {Name}
                </Text>
                <div className={styles.rowValue}>{Value}</div>
                {isNested && <Icon icon={icon} />}
            </div>
            <Text variant={"V2.overline.neutral.regular"} color={!isNested ? "text_Low_Emphasis" : "text_disabled"}>
                {SecondaryText}
            </Text>
        </div>
    );
};

export default memo(InfoDisplay);
